import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { useLanguage } from '@/contexts/LanguageContext';
import { Home, Newspaper, Mail, Users, Eye, FileEdit, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getAuthHeader } from '@/utils/api';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      icon: Home,
      title: 'Annonces de logement',
      value: stats?.listings?.total || 0,
      published: stats?.listings?.published || 0,
      draft: stats?.listings?.draft || 0,
      color: 'bg-blue-50 text-[#127abb]',
      link: '/admin/listings'
    },
    {
      icon: Newspaper,
      title: 'Actualités',
      value: stats?.news?.total || 0,
      published: stats?.news?.published || 0,
      color: 'bg-purple-50 text-purple-600',
      link: '/admin/news'
    },
    {
      icon: Mail,
      title: 'Messages de contact',
      value: stats?.contacts?.total || 0,
      color: 'bg-green-50 text-green-600',
      link: '/admin/contacts'
    },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">📊 Tableau de bord</h1>
          <p className="text-lg text-slate-600">Bienvenue sur l'interface d'administration AILE</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-[#127abb] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {statCards.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Link key={index} to={stat.link}>
                    <div
                      data-testid={`stat-card-${index}`}
                      className="bg-white border border-stone-100 shadow-lg rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-14 h-14 ${stat.color} rounded-xl flex items-center justify-center shadow-sm`}>
                          <Icon size={28} strokeWidth={2} />
                        </div>
                        {stat.draft !== undefined && stat.draft > 0 && (
                          <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                            {stat.draft} brouillon(s)
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-medium text-slate-600 mb-2">{stat.title}</h3>
                      <div className="flex items-baseline gap-3">
                        <p className="text-4xl font-bold text-slate-900">{stat.value}</p>
                        {stat.published !== undefined && (
                          <span className="text-sm text-slate-500 flex items-center gap-1">
                            <Eye size={14} />
                            {stat.published} publié(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gradient-to-br from-[#127abb] to-[#0e6292] text-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <TrendingUp size={24} />
                  <h3 className="text-lg font-semibold">Taux de publication</h3>
                </div>
                <p className="text-3xl font-bold mb-2">
                  {stats?.listings?.total > 0 
                    ? Math.round((stats.listings.published / stats.listings.total) * 100)
                    : 0}%
                </p>
                <p className="text-sm text-blue-100">
                  {stats?.listings?.published} sur {stats?.listings?.total} annonces publiées
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-purple-700 text-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <FileEdit size={24} />
                  <h3 className="text-lg font-semibold">Contenu total</h3>
                </div>
                <p className="text-3xl font-bold mb-2">
                  {(stats?.listings?.total || 0) + (stats?.news?.total || 0)}
                </p>
                <p className="text-sm text-purple-100">
                  Logements + Actualités
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-green-700 text-white rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <Users size={24} />
                  <h3 className="text-lg font-semibold">Engagement</h3>
                </div>
                <p className="text-3xl font-bold mb-2">
                  {stats?.contacts?.total || 0}
                </p>
                <p className="text-sm text-green-100">
                  Messages reçus
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white border border-stone-100 shadow-lg rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">⚡ Actions rapides</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Link to="/admin/listings">
                  <Button className="w-full bg-[#127abb] hover:bg-[#0e6292] rounded-full py-6 text-base shadow-lg hover:shadow-xl transition-all">
                    <Home size={20} className="mr-2" />
                    Gérer les logements
                  </Button>
                </Link>
                <Link to="/admin/news">
                  <Button className="w-full bg-purple-600 hover:bg-purple-700 rounded-full py-6 text-base shadow-lg hover:shadow-xl transition-all">
                    <Newspaper size={20} className="mr-2" />
                    Gérer les actualités
                  </Button>
                </Link>
                <Link to="/admin/contacts">
                  <Button className="w-full bg-green-600 hover:bg-green-700 rounded-full py-6 text-base shadow-lg hover:shadow-xl transition-all">
                    <Mail size={20} className="mr-2" />
                    Voir les messages
                  </Button>
                </Link>
              </div>
            </div>

            {/* Info Card */}
            <div className="mt-8 bg-gradient-to-r from-primary-50 to-blue-50 border-l-4 border-[#127abb] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">💡 Conseil</h3>
              <p className="text-slate-700">
                Utilisez le menu de gauche pour naviguer entre les différentes sections de l'administration. 
                N'oubliez pas de publier vos annonces pour qu'elles soient visibles sur le site public !
              </p>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default Dashboard;